import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import "../styles/category.css";
import { API_BASE_URL, BASE_URL } from "../api/api";

const CategoryPage = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [active, setActive] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { addToCart, updateQuantity, getItemQuantity, activeOrder } = useCart(); // Added updateQuantity & getItemQuantity
  const isOrderActive = !!activeOrder;

  // FETCH CATEGORIES
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/categories`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        console.log("Categories loaded:", data);
        setCategories(data);
        if (data && data.length > 0) {
          setActive(data[0].id);
        }
      } catch (err) {
        console.error("Error fetching categories:", err);
        setError(
          "Failed to load categories. Please check if backend is running.",
        );
      }
    };
    fetchCategories();
  }, []);

  // FETCH MENU ITEMS WHEN CATEGORY CHANGES
  useEffect(() => {
    if (!active) return;

    const fetchMenuItems = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(
          `${API_BASE_URL}/menu-items/category/${active}`,
        );
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        console.log("Menu items loaded for category", active, data);
        setMenuItems(data || []);
      } catch (err) {
        console.error("Error fetching menu items:", err);
        setError("Failed to load menu items. Please try again.");
        setMenuItems([]);
      } finally {
        setLoading(false);
      }
    };

    fetchMenuItems();
  }, [active]);

  // SEARCH FILTER
  const filteredItems = menuItems.filter((item) =>
    item.item_name?.toLowerCase().includes(search.toLowerCase()),
  );

  // Handle quantity change
  const handleQuantityChange = (item, newQuantity, e) => {
    e.stopPropagation();

    if (activeOrder) {
      showMessage("🍽️ Order is in progress", "#ff6b00");
      return;
    }

    if (newQuantity > 0) {
      if (getItemQuantity(item.id)) {
        updateQuantity(item.id, newQuantity);
      } else {
        addToCart(item, newQuantity, null);
      }
    } else {
      updateQuantity(item.id, 0);
    }
  };

  // Helper to show messages
  const showMessage = (text, bgColor = "#0baf9a") => {
    const message = document.createElement("div");
    message.className = "cart-message";
    message.textContent = text;
    message.style.position = "fixed";
    message.style.bottom = "80px";
    message.style.left = "50%";
    message.style.transform = "translateX(-50%)";
    message.style.backgroundColor = bgColor;
    message.style.color = "white";
    message.style.padding = "10px 20px";
    message.style.borderRadius = "25px";
    message.style.zIndex = "1000";
    document.body.appendChild(message);
    setTimeout(() => message.remove(), 2000);
  };

  const handleItemClick = (itemId) => {
    navigate(`/item/${itemId}`);
  };

  // Show error state
  if (error) {
    return (
      <div className="error-state">
        <div className="error-icon">⚠️</div>
        <h3>Connection Error</h3>
        <p>{error}</p>
        <button onClick={() => window.location.reload()}>Retry</button>
      </div>
    );
  }

  // Show loading for categories
  if (categories.length === 0 && !error) {
    return (
      <div className="loading-state">
        <div className="loader"></div>
        <p>Loading categories...</p>
      </div>
    );
  }

  const getCategoryIcon = (categoryName) => {
    const iconMap = {
      Pizza: "🍕",
      Burgers: "🍔",
      Beverages: "🥤",
      Desserts: "🍰",
      Starters: "🍗",
      Salads: "🥗",
      Pasta: "🍝",
      Seafood: "🦞",
    };
    return iconMap[categoryName] || "🍽️";
  };

  return (
    <div className="category-page">
      {/* SEARCH BAR */}
      <div className="search-bar">
        <input
          type="text"
          placeholder="Search food..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="category-layout">
        {/* LEFT SIDEBAR */}
        <div className="category-left">
          {categories.map((cat) => (
            <div
              key={cat.id}
              className={`cat-item ${active === cat.id ? "active" : ""}`}
              onClick={() => setActive(cat.id)}>
              <div className="cat-icon">
                {getCategoryIcon(cat.category_name)}
              </div>
              <span>{cat.category_name}</span>
            </div>
          ))}
        </div>

        {/* RIGHT SIDE */}
        <div className="category-right">
          <h2 className="category-title">
            {categories.find((c) => c.id === active)?.category_name ||
              "Menu Items"}
          </h2>

          {loading ? (
            <div className="loading-spinner">
              <div className="loader"></div>
              <p>Loading items...</p>
            </div>
          ) : (
            <>
              <div className="product-grid">
                {filteredItems.length > 0 ? (
                  filteredItems.map((item) => {
                    const currentQuantity = getItemQuantity(item.id);
                    return (
                      <div
                        className="product-card"
                        key={item.id}
                        onClick={() => handleItemClick(item.id)}>
                        <div className="product-image">
                          <img
                            src={
                              item.image
                                ? `${BASE_URL}/${item.image}`
                                : "https://cdn-icons-png.flaticon.com/512/5787/5787016.png"
                            }
                            alt={item.item_name}
                            onError={(e) => {
                              e.target.src =
                                "https://cdn-icons-png.flaticon.com/512/5787/5787016.png";
                            }}
                          />
                        </div>

                        <div className="product-content">
                          <h4>{item.item_name}</h4>
                          <p className="item-description">
                            {item.description || "Delicious food item"}
                          </p>
                          <div className="bottom-row">
                            <span className="price">₹{item.price}</span>

                            {/* ZOMATO STYLE QUANTITY CONTROLS */}
                            {currentQuantity > 0 ? (
                              <div className="quantity-controls">
                                <button
                                  className="qty-btn minus"
                                  onClick={(e) =>
                                    handleQuantityChange(
                                      item,
                                      currentQuantity - 1,
                                      e,
                                    )
                                  }>
                                  -
                                </button>
                                <span className="qty-count">
                                  {currentQuantity}
                                </span>
                                <button
                                  className="qty-btn plus"
                                  onClick={(e) =>
                                    handleQuantityChange(
                                      item,
                                      currentQuantity + 1,
                                      e,
                                    )
                                  }>
                                  +
                                </button>
                              </div>
                            ) : (
                              <button
                                className="add-btn"
                                onClick={(e) =>
                                  handleQuantityChange(item, 1, e)
                                }>
                                Add
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="empty">
                    <h3>No Items Found 😔</h3>
                    <p>
                      Try searching for something else or select a different
                      category
                    </p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CategoryPage;
